// src/user/UpdateProfile.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useLocation } from "react-router-dom";
import { API } from "../config/apiConfig";
import { clearKYCCache } from "../utils/kycUtils";
import { useTheme } from "@mui/material/styles";
import { getDashboardCardSx } from "../utils/userDashboardShell";
import DashboardPageHeader from "../components/DashboardPageHeader";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  readHomeCache,
  readUserSessionProfileCache,
  writeUserSessionProfileCache,
} from "../utils/userDashboardHomeCache";
import {
  mergeUserRedemptionStatusCache,
  evaluateProfileCompleteFromUser,
  PAKISTAN_DEFAULT_POSTAL_CODE,
  normalizePostalCodeForSave,
} from "../utils/userRedemptionStatusCache";

// Toggle to silence logs
const DEBUG = true;

const normalizePhoneNumber = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value).trim();
  if (!str) return "";
  if (/e[\+\-]/i.test(str)) {
    const num = Number(str);
    if (!Number.isNaN(num)) {
      return num.toLocaleString("fullwide", { useGrouping: false });
    }
  }
  return str;
};

const SectionCard = ({ title, description, children }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 2,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
      p: { xs: 2, md: 3 },
    }}
  >
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        )}
      </Box>
      {children}
    </Stack>
  </Paper>
);

function UpdateProfile({ userId: propUserId }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // ---------- state ----------
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wasMissingCountry, setWasMissingCountry] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(false);

  // form fields
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  
  // KYC fields
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [ssnLast4, setSsnLast4] = useState("");

  // Location (ids as strings)
  const [countryId, setCountryId] = useState("");               // Country of Residence
  const [regionId, setRegionId] = useState("");                 // Region
  const [countryOfOriginId, setCountryOfOriginId] = useState("");// Country of Origin

  // Demographics
  const [sexId, setSexId] = useState("");
  const [ethnicityId, setEthnicityId] = useState("");

  // lookups (raw from API)
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [sexes, setSexes] = useState([]);
  const [ethnicities, setEthnicities] = useState([]);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showError = (message) => setSnackbar({ open: true, message, severity: "error" });

  // ---------- resolve userId (prop → JWT) ----------
  const userId = useMemo(() => {
    if (propUserId) return propUserId;
    const token = localStorage.getItem("token");
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded?.UserId || decoded?.userId || decoded?.id || decoded?.sub || null;
    } catch {
      return null;
    }
  }, [propUserId]);

  // ---------- helpers ----------
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const extractList = (data, keys = []) => {
    if (Array.isArray(data)) return data;
    for (const k of keys) if (data && Array.isArray(data[k])) return data[k];
    return [];
  };

  // Name normalizer (only used for rare fallbacks)
  const norm = (s) =>
    String(s ?? "")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const findByNameInsensitive = (options, name) => {
    if (!name) return null;
    const needle = norm(name);
    let hit = options.find((o) => norm(o.label) === needle);
    if (hit) return hit;
    hit = options.find((o) => norm(o.label).includes(needle));
    return hit || null;
  };

  // Normalizers — support both {id,name} and DB-style keys
  const mapCountry = (c) => ({
    id: String(c.id ?? c.CountryId ?? c.countryId),
    label: String(c.name ?? c.CountryName ?? c.countryName ?? ""),
  });
  const mapRegion = (r) => ({
    id: String(r.id ?? r.RegionId ?? r.regionId),
    label: String(r.name ?? r.RegionName ?? r.regionName ?? ""),
  });
  const mapEthnicity = (e) => ({
    id: String(e.id ?? e.EthnicityId ?? e.ethnicityId),
    label: String(e.name ?? e.EthnicityName ?? e.ethnicityName ?? ""),
  });
  const mapSex = (s) => ({
    id: String(s.id ?? s.SexId ?? s.sexId),
    label: String(s.name ?? s.SexName ?? s.sexName ?? ""),
  });

  // Derived, normalized options for UI
  const countryOptions = useMemo(() => (countries || []).map(mapCountry), [countries]);
  const regionOptions = useMemo(() => (regions || []).map(mapRegion), [regions]);
  const ethnicityOptions = useMemo(() => (ethnicities || []).map(mapEthnicity), [ethnicities]);
  const sexOptions = useMemo(() => (sexes || []).map(mapSex), [sexes]);

  // Selected objects (for Autocomplete)
  const selectedCountry = countryOptions.find((o) => o.id === String(countryId)) || null;
  const selectedRegion = regionOptions.find((o) => o.id === String(regionId)) || null;
  const selectedOrigin = countryOptions.find((o) => o.id === String(countryOfOriginId)) || null;
  const selectedEthnicity =
    ethnicityOptions.find((o) => o.id === String(ethnicityId)) || null;

  // Find US and Pakistan country IDs dynamically
  const usCountryId = useMemo(() => {
    const us = countryOptions.find((c) => 
      norm(c.label).includes("united states") || 
      norm(c.label) === "usa" ||
      norm(c.label) === "us"
    );
    return us ? us.id : null;
  }, [countryOptions]);

  const pakistanCountryId = useMemo(() => {
    const pk = countryOptions.find((c) => 
      norm(c.label).includes("pakistan") || 
      norm(c.label) === "pk"
    );
    return pk ? pk.id : null;
  }, [countryOptions]);

  // Check if current country is US or Pakistan
  const isUnitedStates = countryId && usCountryId && String(countryId) === String(usCountryId);
  const isPakistan = countryId && pakistanCountryId && String(countryId) === String(pakistanCountryId);

  // ---------- lookups ----------
  const loadCountries = async () => {
    const res = await axios.get(API.countries, { headers: authHeader });
    if (DEBUG) {
      console.log("🌍 countries raw:", res.data);
      const arr = extractList(res.data, ["countries", "data", "recordset"]);
      console.log("🌍 countries sample[0..2]:", arr.slice(0, 3));
    }
    const list = extractList(res.data, ["countries", "data", "recordset"]);
    setCountries(list);
    if (DEBUG) console.log("🌍 countries normalized sample:", list.slice(0, 3).map(mapCountry));
    return list;
  };

  const loadSexes = async () => {
    const res = await axios.get(API.sexes, { headers: authHeader });
    if (DEBUG) console.log("⚧ sexes raw:", res.data);
    const list = extractList(res.data, ["sexes", "data", "recordset"]);
    setSexes(list);
    if (DEBUG) console.log("⚧ sexes normalized:", list.map(mapSex));
    return list;
  };

  const loadEthnicities = async () => {
    const res = await axios.get(API.ethnicities, { headers: authHeader });
    if (DEBUG) console.log("🧬 ethnicities raw:", res.data);
    const list = extractList(res.data, ["ethnicities", "data", "recordset"]);
    setEthnicities(list);
    return list;
  };

  // Returns list so we can preselect Region (we have RegionId now, but still need the list)
  const loadRegions = async (cid) => {
    if (!cid) {
      setRegions([]);
      setRegionId("");
      return [];
    }
    const res = await axios.get(API.regions(cid), { headers: authHeader });
    if (DEBUG) console.log(`🗺️ regions raw (countryId=${cid}):`, res.data);
    const list = extractList(res.data, ["regions", "data", "recordset"]);
    setRegions(list);
    if (DEBUG) console.log("🗺️ regions normalized sample:", list.slice(0, 3).map(mapRegion));
    return list;
  };

  // ---------- profile ----------
  const loadProfile = async () => {
    if (!userId) {
      setLoading(false);
      setSnackbar({ open: true, message: "User not found", severity: "error" });
      return;
    }
    
    // Check if country was missing before loading
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        const countryOfResidenceId = decoded.countryOfResidenceId || decoded.CountryOfResidenceId || null;
        setWasMissingCountry(!countryOfResidenceId);
      } catch (e) {
        console.error('Error checking token:', e);
      }
    }
    
    let profilePayload =
      readUserSessionProfileCache(userId)?.profile ||
      readHomeCache(userId)?.raw?.profile;
    if (!profilePayload) {
      const res = await axios.get(API.userProfile(userId), { headers: authHeader });
      if (DEBUG) console.log("👤 userProfile raw:", res.data);
      profilePayload = res.data;
      writeUserSessionProfileCache(userId, profilePayload);
    }
    const u = profilePayload?.user || profilePayload || {};

    setUsername(u.Username ?? u.UserName ?? u.username ?? "");
    setEmail(u.Email ?? u.email ?? "");
    setFirstName(u.FirstName ?? u.firstName ?? "");
    setLastName(u.LastName ?? u.lastName ?? "");

    const mobile =
      u.MobileNumber ??
      u.Mobile ??
      u.mobileNumber ??
      u.mobile ??
      u.PhoneNumber ??
      u.phoneNumber ??
      "";
    setPhoneNumber(normalizePhoneNumber(mobile));
    
    // KYC fields
    if (DEBUG) console.log("📅 Raw KYC data from API:", {
      DateOfBirth: u.DateOfBirth,
      dateOfBirth: u.dateOfBirth,
      AddressLine1: u.AddressLine1,
      addressLine1: u.addressLine1,
      City: u.City,
      city: u.city,
      PostalCode: u.PostalCode,
      postalCode: u.postalCode,
      SsnLast4: u.SsnLast4,
      ssnLast4: u.ssnLast4
    });
    
    // Format date for HTML date input (YYYY-MM-DD)
    const rawDate = u.DateOfBirth ?? u.dateOfBirth ?? "";
    let formattedDate = "";
    if (rawDate) {
      try {
        const date = new Date(rawDate);
        if (!isNaN(date.getTime())) {
          formattedDate = date.toISOString().split('T')[0]; // YYYY-MM-DD format
        }
      } catch (e) {
        console.warn("Could not format date:", rawDate, e);
      }
    }
    setDateOfBirth(formattedDate);
    setAddressLine1(u.AddressLine1 ?? u.addressLine1 ?? "");
    setCity(u.City ?? u.city ?? "");
    const rawPostal = u.PostalCode ?? u.postalCode ?? "";
    const residenceName = String(
      u.CountryOfResidence ?? u.countryOfResidence ?? ""
    ).toLowerCase();
    const loadedAsPakistan =
      String(u.CountryCode ?? u.countryCode ?? "").toUpperCase() === "PK" ||
      residenceName.includes("pakistan");
    setPostalCode(
      loadedAsPakistan && !String(rawPostal).trim()
        ? PAKISTAN_DEFAULT_POSTAL_CODE
        : rawPostal
    );
    setSsnLast4(u.SsnLast4 ?? u.ssnLast4 ?? "");
    
    if (DEBUG) console.log("📅 Date processing:", {
      rawDate: u.DateOfBirth ?? u.dateOfBirth ?? "",
      formattedDate: formattedDate,
      finalState: formattedDate
    });

    // IDs now provided in your JSON:
    const coResIdStr   = (u.CountryOfResidenceId ?? u.CountryOfResidenceID ?? u.CountryId ?? u.countryId ?? "") + "";
    const sexIdStr     = (u.SexId ?? u.SexID ?? u.sexId ?? "") + "";
    const originIdStr  = (u.CountryOfOriginID ?? u.CountryOfOriginId ?? u.countryOfOriginId ?? "") + "";
    const regionIdStr  = (u.RegionId ?? u.RegionID ?? u.regionId ?? "") + "";
    const ethnicityStr = (u.EthnicityId ?? u.EthnicityID ?? u.ethnicityId ?? "") + "";

    if (DEBUG) console.log("🧭 IDs from profile:", {
      CountryOfResidenceId: coResIdStr,
      SexId: sexIdStr,
      CountryOfOriginId: originIdStr,
      RegionId: regionIdStr,
      EthnicityId: ethnicityStr,
    });

    setCountryId(coResIdStr || "");
    setSexId(sexIdStr || "");
    setCountryOfOriginId(originIdStr || "");
    setEthnicityId(ethnicityStr || "");

    // Load regions for the selected Country-of-Residence, then apply RegionId
    if (coResIdStr) {
      await loadRegions(coResIdStr);
      // ensure the incoming RegionId exists in the list; otherwise try a name fallback
      if (regionIdStr && (regions.length === 0 || !regions.some(r => String(r.id ?? r.RegionId ?? r.regionId) === regionIdStr))) {
        // optional fallback by name if your API ever returns RegionName in future
        const rName = u.RegionName ?? u.regionName ?? u.Region ?? u.region ?? "";
        const match = findByNameInsensitive((regions || []).map(mapRegion), rName);
        setRegionId(match ? match.id : regionIdStr);
      } else {
        setRegionId(regionIdStr || "");
      }
    } else {
      setRegions([]);
      setRegionId("");
    }

    if (DEBUG) {
      console.log("✅ Preselected ids (state):", {
        sexId: sexIdStr || "",
        countryOfOriginId: originIdStr || "",
        countryId: coResIdStr || "",
        regionId: regionIdStr || "",
        ethnicityId: ethnicityStr || "",
      });
    }
  };

  // ---------- effects ----------
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await Promise.all([
          loadProfile(),
          loadCountries(),
          loadSexes(),
          loadEthnicities(),
        ]);
      } catch (e) {
        console.error("❌ Error initializing UpdateProfile:", e);
        setSnackbar({ open: true, message: "Error loading profile", severity: "error" });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // If user changes Country of Residence, reload regions & clear selection
  useEffect(() => {
    if (countryId) loadRegions(countryId);
    else {
      setRegions([]);
      setRegionId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId]);

  // Pakistan: show default postal code when field would otherwise be empty
  useEffect(() => {
    if (isPakistan) {
      setPostalCode((prev) => (String(prev).trim() ? prev : PAKISTAN_DEFAULT_POSTAL_CODE));
    }
  }, [isPakistan, countryId]);

  // Check if profile is complete based on required fields
  useEffect(() => {
    // Required fields based on validation in handleSave
    const hasRequiredFields = 
      firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      sexId !== "" &&
      countryOfOriginId !== "" &&
      countryId !== "" &&
      dateOfBirth !== "" &&
      addressLine1.trim() !== "" &&
      city.trim() !== "";
    
    setIsProfileComplete(hasRequiredFields);
  }, [firstName, lastName, sexId, countryOfOriginId, countryId, dateOfBirth, addressLine1, city]);

  // ---------- save ----------
  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!userId) return showError("User not found");

    // Validate country of residence if it was missing
    if (wasMissingCountry && !countryId) {
      return showError("Country of residence is required. Please select your country.");
    }

    if (!dateOfBirth) return showError("Date of birth is required.");
    if (!addressLine1.trim()) return showError("Street address is required.");
    if (!city.trim()) return showError("City is required.");
    if (!isPakistan && !postalCode.trim()) return showError("Postal code is required.");

    const resolvedPostal = normalizePostalCodeForSave(postalCode, isPakistan);

    const payload = {
      userId: Number(userId),
      firstName: firstName || "",
      lastName: lastName || "",
      mobileNumber: phoneNumber || "",
      countryOfResidence: countryId ? Number(countryId) : null,
      regionId: regionId ? Number(regionId) : null,
      countryOfOrigin: countryOfOriginId ? Number(countryOfOriginId) : null,
      sexId: sexId ? Number(sexId) : null,
      ethnicity: ethnicityId ? Number(ethnicityId) : null,
      // KYC fields
      dateOfBirth: dateOfBirth || "",
      addressLine1: addressLine1 || "",
      city: city || "",
      postalCode: resolvedPostal,
      ssnLast4: ssnLast4 || "",
    };

    try {
      if (DEBUG) console.log("📤 updateProfile payload:", payload);
      if (DEBUG) console.log("📅 Date of Birth details:", {
        dateOfBirth,
        type: typeof dateOfBirth,
        length: dateOfBirth?.length,
        isEmpty: !dateOfBirth
      });
      const res = await axios.post(API.updateProfile, payload, { headers: authHeader });
      if (DEBUG) console.log("📥 updateProfile response:", res.data);
      const cachedProfile = readUserSessionProfileCache(userId)?.profile;
      const cachedUser = cachedProfile?.user || cachedProfile || {};
      const mergedUser = {
        ...cachedUser,
        FirstName: payload.firstName,
        LastName: payload.lastName,
        MobileNumber: payload.mobileNumber,
        CountryOfResidenceId: payload.countryOfResidence,
        CountryOfResidence: selectedCountry?.label ?? cachedUser.CountryOfResidence ?? "",
        CountryCode: isPakistan ? "PK" : (cachedUser.CountryCode ?? cachedUser.countryCode ?? ""),
        RegionId: payload.regionId,
        CountryOfOriginID: payload.countryOfOrigin,
        SexID: payload.sexId,
        EthnicityID: payload.ethnicity,
        DateOfBirth: payload.dateOfBirth,
        AddressLine1: payload.addressLine1,
        City: payload.city,
        PostalCode: payload.postalCode,
      };
      writeUserSessionProfileCache(userId, { user: mergedUser });

      // Persist "profile complete" so the redemption notice and login
      // prefetcher can skip the profile fetch on subsequent loads.
      if (evaluateProfileCompleteFromUser(mergedUser)) {
        mergeUserRedemptionStatusCache(userId, { profileComplete: true });
      }

      // Check if country of residence was updated
      const countryWasUpdated = payload.countryOfResidence !== null && payload.countryOfResidence !== undefined;
      const hadNoCountryBefore = wasMissingCountry;

      setSnackbar({
        open: true,
        message: res.data?.message || "Profile updated successfully",
        severity: "success",
      });
      
      // If country was updated (especially if it was missing before), refresh the page
      // to get new JWT token with updated country and re-check KYC requirements
      if (countryWasUpdated && (hadNoCountryBefore || res.data?.token)) {
        console.log('🔄 Country of residence updated, refreshing to check KYC requirements...');
        
        // Clear KYC cache so it re-checks with new country
        clearKYCCache();
        
        // If backend returns new token, update it
        if (res.data?.token) {
          localStorage.setItem('token', res.data.token);
        }
        
        // Reload page after a short delay to show success message
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (err) {
      console.error("❌ Update profile error:", err.response?.data || err.message);
      setSnackbar({
        open: true,
        message:
          err.response?.data?.error || err.response?.data?.message || "Error updating profile",
        severity: "error",
      });
    }
  };

  // ---------- render ----------
  return (
    <Box p={2} component="form" autoComplete="off" onSubmit={handleSave}>
      <DashboardPageHeader
        icon={PersonOutlineIcon}
        title="My profile"
        subtitle="Keep your account details current for a smooth experience across the app."
      />
      <Paper
        elevation={0}
        sx={{
          p: 4,
          maxWidth: 1080,
          mx: "auto",
          ...getDashboardCardSx(theme),
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
          Account details
        </Typography>
        {loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading latest profile options...
            </Typography>
          </Box>
        )}
        
        {/* Profile completion status */}
        {!loading && (
          <Alert 
            severity={isProfileComplete ? "success" : "info"} 
            sx={{ mb: 3 }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              {isProfileComplete ? "Profile completed" : "Complete Your Profile"}
            </Typography>
            <Typography variant="body2">
              {isProfileComplete 
                ? "Profile completed" 
                : "Please update your profile to start using the portal and the apps."}
            </Typography>
          </Alert>
        )}

        <Stack spacing={3} mt={3}>
          <SectionCard title="Account">
        <Grid container spacing={2}>
          <Grid xs={12} md={6}>
            <TextField label="Username" fullWidth value={username} disabled size="medium" />
          </Grid>
          <Grid xs={12} md={6}>
            <TextField label="Email" type="email" fullWidth value={email} disabled size="medium" />
          </Grid>
        </Grid>
          </SectionCard>

          <SectionCard
            title="Personal & Demographics"
            description="Tell us a little about yourself for a more personalized experience."
          >
        <Grid container spacing={2}>
          <Grid xs={12} md={6}>
            <TextField
              label="First Name"
              fullWidth
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              size="medium"
            />
          </Grid>
          <Grid xs={12} md={6}>
            <TextField
              label="Last Name"
              fullWidth
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              size="medium"
            />
          </Grid>
              <Grid xs={12} md={4}>
                <FormControl fullWidth size="medium" sx={{ minWidth: 220 }}>
                  <InputLabel>Gender</InputLabel>
              <Select
                    label="Gender"
                value={sexId}
                onChange={(e) => setSexId(String(e.target.value))}
              >
                {sexOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
              <Grid xs={12} md={4}>
            <Autocomplete
              fullWidth
                  autoHighlight
              options={ethnicityOptions}
              value={selectedEthnicity}
              onChange={(_, val) => setEthnicityId(val ? val.id : "")}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                    <TextField {...params} label="Ethnicity" size="medium" sx={{ minWidth: 220 }} />
              )}
            />
          </Grid>
              <Grid xs={12} md={4}>
            <Autocomplete
              fullWidth
                  autoHighlight
              options={countryOptions}
              value={selectedOrigin}
              onChange={(_, val) => setCountryOfOriginId(val ? val.id : "")}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                    <TextField {...params} label="Country of Origin" size="medium" sx={{ minWidth: 220 }} />
              )}
            />
          </Grid>
        </Grid>
          </SectionCard>

          <SectionCard
            title="KYC Information"
            description="These details are required for identity verification and compliance."
          >
        <Grid container spacing={2}>
          <Grid xs={12} md={isUnitedStates ? 6 : 12}>
            <TextField
              label="Date of Birth (DOB)"
              type="date"
              fullWidth
                  required
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="medium"
                  inputProps={{ autoComplete: "new-birthday" }}
              helperText="Enter your date of birth"
            />
          </Grid>
          {isUnitedStates && (
            <Grid xs={12} md={6}>
              <TextField
                label="SSN Last 4 Digits (US only)"
                fullWidth
                value={ssnLast4}
                onChange={(e) => setSsnLast4(e.target.value)}
                placeholder="1234"
                size="medium"
                inputProps={{ maxLength: 4 }}
              />
            </Grid>
          )}
              <Grid xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  autoHighlight
                  options={countryOptions}
                  value={selectedCountry}
                  onChange={(_, val) => {
                    const newId = val ? val.id : "";
                    if (DEBUG) console.log("🌐 Country of Residence changed →", newId);
                    setCountryId(newId);
                    setRegionId("");
                    if (newId) loadRegions(newId);
                    else setRegions([]);
                  }}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Country of Residence" 
                      size="medium" 
                      sx={{ minWidth: 260 }}
                      required={wasMissingCountry}
                      error={wasMissingCountry && !countryId}
                      helperText={wasMissingCountry && !countryId ? "Country of residence is required" : ""}
                    />
                  )}
                />
              </Grid>
              <Grid xs={12} md={6}>
            <TextField
              label="Street Address"
              fullWidth
                  required
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="123 Main Street"
              size="medium"
                  name="new-street-address"
                  autoComplete="new-address"
            />
          </Grid>
          <Grid xs={12} md={6}>
            <TextField
              label="City"
              fullWidth
                  required
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="New York"
              size="medium"
                  name="new-city"
                  autoComplete="new-city"
            />
          </Grid>
              <Grid xs={12} md={4}>
                <TextField
                  label="Postal Code"
                  fullWidth
                  required={!isPakistan}
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder={isPakistan ? PAKISTAN_DEFAULT_POSTAL_CODE : "10001"}
                  helperText={
                    isPakistan
                      ? `Optional for Pakistan — defaults to ${PAKISTAN_DEFAULT_POSTAL_CODE} if left blank`
                      : ""
                  }
                  size="medium"
                  name="new-postal-code"
                  autoComplete="new-postal-code"
                />
              </Grid>
              <Grid xs={12} md={4}>
                <TextField
                  label="Mobile / Phone Number"
                  fullWidth
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+44 7123 456789"
                  size="medium"
            />
          </Grid>
        </Grid>
          </SectionCard>
        </Stack>

        {/* Actions */}
        <Box mt={4} display="flex" gap={2} justifyContent="flex-end">
          <Button
            type="button"
            variant="outlined"
            onClick={() => {
              if (DEBUG) console.log("🔁 Resetting form from server…");
              loadProfile(countries, sexes);
            }}
            disabled={saving}
          >
            Reset
          </Button>
          <Button type="submit" variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={22} color="inherit" /> : "Save Changes"}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default UpdateProfile;
